import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, Space, Button } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { plAppConfig, PLAPP } from '../config/plAppConfig';
import { getAppUrlInfo } from '../utils/urls';

const { Title, Text } = Typography;

const CommonDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isLocalhost } = getAppUrlInfo();

  // Generate app URLs with dashboard paths
  const getAppUrl = (app: PLAPP) => {
    const baseUrl = isLocalhost ? `http://localhost:3000` : `https://accounts.parkinglap.com`;
    
    // For apps with dashboards, append the dashboard path
    switch (app.key) {
      case 'accounts':
        return `${baseUrl}/accounts`;
      case 'finance':
        return `${baseUrl}/finance`;
      case 'masters':
        return `${baseUrl}/master-data`;
      case 'leads':
        return `${baseUrl}/leads`;
      default:
        // For other apps without dashboards, use base URL
        return baseUrl;
    }
  };

  const handleAppClick = (app: PLAPP) => {
    const url = getAppUrl(app);
    if (app.key === 'accounts' || app.key === 'finance' || app.key === 'masters' || app.key === 'leads') {
      // Navigate within the same app
      navigate(app.key === 'masters' ? '/master-data' : `/${app.key}`);
    } else {
      // Open external apps in new tab
      window.open(url, '_blank');
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="container mx-auto px-6 py-12">
        {/* Header Section */}
        <div className="text-center mb-16">
          {/* Logo */}
          <div className="mb-6">
            <img 
              src="https://parkinglap.com/assets/images/logo.png" 
              alt="Parking Lap Logo" 
              className="h-12 mx-auto"
            />
          </div>
          
          <Title 
            level={4} 
            className="!text-4xl !font-bold !text-gray-800 !mb-4"
          >
            Parking Lap Applications
          </Title>
          <Text className="text-lg text-gray-600 max-w-2xl mx-auto block">
            Welcome to the Parking Lap ecosystem. Select an application below to access your desired module and start managing your business operations.
          </Text>
        </div>

        {/* Applications Grid */}
        <Row gutter={[24, 24]} justify="center" className="mb-16">
          {plAppConfig.map((app) => (
            <Col 
              key={app.key} 
              xs={24} 
              sm={12} 
              md={8} 
              lg={6} 
              xl={4}
            >
              <Card
                hoverable
                className="h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-6 flex flex-col items-center justify-center"
                onClick={() => handleAppClick(app)}
              >
                {/* Icon Container */}
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-transform duration-300 hover:scale-110"
                  style={{ backgroundColor: `${app.color}20` }}
                >
                  <FontAwesomeIcon
                    icon={app.icon!}
                    size="lg"
                    style={{ color: app.color }}
                  />
                </div>
                
                {/* App Info */}
                <div className="text-center">
                  <Title level={5} className="!mb-3 !text-gray-800 !font-semibold !text-sm">
                    {app.name}
                  </Title>
                </div>

                {/* Action Button */}
                <Button 
                  type="primary" 
                  size="small"
                  className="mt-3 !rounded-full !px-6"
                  style={{ backgroundColor: app.color, borderColor: app.color }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAppClick(app);
                  }}
                >
                  {app.key === 'accounts' || app.key === 'finance' || app.key === 'masters' || app.key === 'leads' ? 'Open' : 'Launch'}
                </Button>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Footer Section */}
        <div className="text-center">
            <Space direction="vertical" size="middle">
              <Text className="text-gray-600 text-base">
                💡 <strong>Quick Tip:</strong> Click on any application card above to access the respective module
              </Text>
              <div className="flex justify-center gap-4 text-sm text-gray-500">
                <span>🔐 Secure Access</span>
                <span>📱 Mobile Friendly</span>
                <span>⚡ Fast Performance</span>
              </div>
            </Space>
        </div>
      </div>
    </div>
  );
};

export default CommonDashboard;
